import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Multi-select for Admin Categories with search.
 * value: array of category ids (strings)
 * onChange: (nextIds: string[]) => void
 */
export const MultiAdminCategorySelector = ({
  value = [],
  onChange,
  label = 'Admin Categories'
}) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // [{id,name}]
  const hydratedRef = useRef(false);

  // Search categories (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCategories(searchQuery);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchCategories = async (q = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin-categories/search`, {
        params: { q }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch admin categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hydrate selected labels from ids (edit mode) — fetch full list once
  useEffect(() => {
    const ids = (value || []).map(String);
    // remove ones no longer in value
    setSelected((prev) => prev.filter((p) => ids.includes(p.id)));

    const missing = ids.filter((id) => !selected.find((s) => s.id === id));
    if (missing.length === 0) return;

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      axios
        .get(`${API}/admin-categories`, { params: { limit: 1000 } })
        .then((r) => {
          // Backend now returns { data, pagination } instead of a bare array.
          const all = Array.isArray(r.data?.data) ? r.data.data : (Array.isArray(r.data) ? r.data : []);
          setSelected((prev) => {
            const byId = new Map(prev.map((p) => [p.id, p]));
            ids.forEach((id) => {
              if (!byId.has(id)) {
                const found = all.find((c) => String(c.id) === id);
                byId.set(id, { id, name: found?.name || id });
              }
            });
            return Array.from(byId.values()).filter((x) => ids.includes(x.id));
          });
        })
        .catch(() => {
          setSelected((prev) => {
            const byId = new Map(prev.map((p) => [p.id, p]));
            ids.forEach((id) => {
              if (!byId.has(id)) byId.set(id, { id, name: id });
            });
            return Array.from(byId.values()).filter((x) => ids.includes(x.id));
          });
        });
    }
    // eslint-disable-next-line
  }, [value]);

  const toggle = (cat) => {
    const exists = value.includes(cat.id);
    const next = exists
      ? value.filter((id) => id !== cat.id)
      : [...value, cat.id];
    onChange(next);
    setSelected((prev) => {
      if (exists) return prev.filter((p) => p.id !== cat.id);
      if (prev.find((p) => p.id === cat.id)) return prev;
      return [...prev, { id: cat.id, name: cat.name }];
    });
  };

  const removeOne = (id) => {
    onChange(value.filter((v) => v !== id));
    setSelected((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div data-testid="restaurant-admin-categories-wrapper">
      <Label>{label}</Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            data-testid="restaurant-admin-categories-trigger"
            variant="outline"
            role="combobox"
            className="w-full justify-between mt-2"
          >
            {value.length > 0
              ? `${value.length} categor${value.length > 1 ? 'ies' : 'y'} selected`
              : 'Select admin categories...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[360px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search admin category..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              data-testid="restaurant-admin-categories-search"
            />
            <CommandEmpty>
              {loading ? 'Loading...' : 'No category found.'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {categories.map((cat) => {
                const checked = value.includes(cat.id);
                return (
                  <CommandItem
                    key={cat.id}
                    value={cat.name?.toLowerCase() || cat.id}
                    onSelect={() => toggle(cat)}
                    data-testid={`restaurant-admin-cat-option-${cat.id}`}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        checked ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <div className="font-medium">{cat.name}</div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div
          className="mt-2 flex flex-wrap gap-1.5"
          data-testid="restaurant-admin-categories-chips"
        >
          {selected.map((s) => (
            <Badge
              key={s.id}
              data-testid={`restaurant-admin-cat-chip-${s.id}`}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {s.name}
              <button
                type="button"
                className="rounded-full hover:bg-gray-200 p-0.5"
                onClick={() => removeOne(s.id)}
                aria-label="remove"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiAdminCategorySelector;
